import 'react-table-hoc-fixed-columns/lib/styles.css';
import 'react-table/react-table.css';

import { Paper } from '@mui/material';
import { DateWrapper, HeaderAndContainer, Timespan, vars } from '@trucktrax/trucktrax-common';
import {
  PlantDto,
  PositionDto,
  ProductLine,
  TicketDto,
  UrlKeyDto,
  VoidFunc,
} from '@trucktrax/trucktrax-ts-common';
import cx from 'classnames';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import ReactTable from 'react-table';
import withFixedColumns from 'react-table-hoc-fixed-columns';

import { isEqual } from 'lodash';
import { DATASOURCE, TICKETS_PATH } from '../../../constants/apiConstants';
import { INCOMING_MATERIALS_FLAG } from '../../../constants/appConstants';
import { hasFeatureFlagEditAccess } from '../../../util/featureFlagUtil';
import { RegionAccessMap } from '../../../util/permissionUtil';
import { getDefaultPlant, setCurrentPlant } from '../../../services/plantsService';
import {
  fetchCurrentTicketList,
  saveFilters,
  selectTruckMarker,
} from '../../../services/ticketsService';
import { openPopup, selectMarker } from '../../../store/actions/mapviewActions';
import {
  clearSelectedTicket,
  selectTicket,
  setCurrentTicketsColumns,
} from '../../../store/actions/ticketActions';
import { PlantFilterOption } from '../../../store/reducers/mapFilterReducers';
import {
  selectedPlantUrlsSelector,
  ticketsMatchingMapFiltersSelector,
} from '../../../store/selectors/plantSelectors';
import {
  ConnectedDispatchFunction,
  TicketListFilters,
  TicketStateSwitcherType,
  TicketStateValue,
} from '../../../types';
import { getTicketingBaseUrl } from '../../../util/apiUtil';
import { getIdFromUrl, isEmptyArray } from '../../../util/appUtil';
import { filterBySearchTerm } from '../../../util/ticketUtil';
import DataTableContent from '../DataTableContent';
import DateRangePicker, { DateRangePickerState } from '../DateRangePicker';
import { RefreshCurrentTicketList } from './RefreshCurrentTicketList';
import styles from './TicketList.module.css';
import currentTicketColumns from './ticketListConfigurations/currentTickets';
import pastTicketColumns from './ticketListConfigurations/pastTickets';
import {
  ColumnProjection,
  GetColumnsProps,
  RowInfo,
} from './ticketListConfigurations/ticketListConfigurations.types';
import TicketSearch from './TicketSearch';
import TicketStateSwitcher from './TicketStateSwitcher';

const ReactTableFixedColumns = withFixedColumns(ReactTable) as any;

/* TSLU = Time Since Last Update */
export const MAX_TSLU_IN_SECS = 15 * 60 * 60;
export const ALERT_THRESHOLD_TSLU_IN_SECS = 5 * 60; // 5 minutes
const TIMER_REFRESH_PERIOD_MS = 5000; // 5 seconds

const headerStyle = {
  fontWeight: 'bold',
  fontSize: '1.2em',
  fontFamily: vars.fontFamilyBold,
  WebkitFontSmoothing: 'antialiased',
  MozOsxFontSmoothing: 'antialiased',
};

const cellStyle = {
  fontSize: '1.5em',
  height: '3.1em',
  lineHeight: '2.5em',
};

export class TicketList extends Component<TicketListProps, TicketListState> {
  static defaultProps = {
    positions: [],
    plantList: [],
    plantMapFilter: [],
  };

  baseUrl = getTicketingBaseUrl() + TICKETS_PATH;

  constructor(props: TicketListProps) {
    super(props);

    const { currentRegion, ticketsList, selectedProductLine, ticketListFilters } = this.props;
    const shouldFetchTicketList =
      !this.props.activeTicketsOverride && (!ticketsList || !ticketsList?.length);
    if (shouldFetchTicketList) {
      const { selectedPlantUrlsList } = this.props;
      if (selectedPlantUrlsList.length > 0) {
        this.props.fetchCurrentTicketList(
          currentRegion?.url,
          selectedProductLine,
          undefined,
          selectedPlantUrlsList,
        );
      } else {
        this.props.fetchCurrentTicketList(currentRegion?.url, selectedProductLine);
      }
    }

    // Timer to update TSLU every TIMER_REFRESH_PERIOD_MS seconds
    const timeSinceLastUpdateTimer = global.setInterval(
      () => this.refreshTimeSinceLastUpdate(),
      TIMER_REFRESH_PERIOD_MS,
    );

    let ticketStateValue =
      props.ticketStateSwitcherType === TicketStateSwitcherType.Tabs
        ? TicketStateValue.Active
        : TicketStateValue.AllTickets;

    if (ticketListFilters?.ticketStateValue) {
      ticketStateValue = ticketListFilters.ticketStateValue;
    }

    const startOfToday = DateWrapper.now.startOfDay;

    this.state = {
      // if you don't use .startOf, the date will be wrong depending on the time.
      filterDateEnd: ticketListFilters?.filterDateEnd ?? startOfToday.date,
      filterDateStart: ticketListFilters?.filterDateStart ?? startOfToday.previousDay.date,
      searchTerm: ticketListFilters?.searchTerm ?? '',
      selected: null,
      ticketStateValue,
      timeSinceLastUpdateCounter: 0,
      timeSinceLastUpdateTimer,
    };
  }

  async componentDidMount() {
    if (!this.props.currentPlant) {
      const currentPlant =
        this.props.currentPlant ?? (await this.props.getDefaultPlant(this.props.currentUser!));
      // set the current plant as the currentPlant in redux from the user's default if it doesn't exist
      this.props.setCurrentPlant(currentPlant);
    }
  }

  componentDidUpdate(prevProps: TicketListProps) {
    const { currentRegion, selectedProductLine, currentPlant, selectedPlantUrlsList } = this.props;
    const { ticketStateValue } = this.state;

    if (
      prevProps?.currentRegion?.url !== currentRegion?.url ||
      prevProps?.selectedProductLine !== selectedProductLine ||
      prevProps?.currentPlant !== currentPlant
    ) {
      this.clearSearchText();
    }
    if (
      !isEqual(prevProps.selectedPlantUrlsList, selectedPlantUrlsList) &&
      ticketStateValue === TicketStateValue.Active
    ) {
      const { selectedPlantUrlsList: plantsFilter } = this.props;
      if (plantsFilter.length > 0) {
        this.props.fetchCurrentTicketList(
          currentRegion?.url,
          selectedProductLine,
          undefined,
          plantsFilter,
        );
      } else {
        this.props.fetchCurrentTicketList(currentRegion?.url, selectedProductLine);
      }
    }
  }

  // Clear Time Since Last Update Timer on unmount
  componentWillUnmount() {
    clearInterval(this.state.timeSinceLastUpdateTimer);
  }

  saveFilters = () => {
    const { ticketStateValue, filterDateStart, filterDateEnd, searchTerm } = this.state;

    this.props.saveFilters({
      ticketStateValue,
      filterDateStart,
      filterDateEnd,
      searchTerm,
    });
  };

  static getSecondsSinceLastUpdate = (row: TicketDto, positions: PositionDto[]) => {
    const filteredPositionByTruck =
      positions?.filter(p => p.trackedObject && p.trackedObject.url === row.truck?.url) ?? [];

    if (filteredPositionByTruck.length === 0) {
      return 0;
    }

    const lastUpdateTS = filteredPositionByTruck[0].timestamp;
    let timeDiffInSecs = DateWrapper.differenceBetween(DateWrapper.now, lastUpdateTS).asSeconds();
    timeDiffInSecs = timeDiffInSecs > MAX_TSLU_IN_SECS ? MAX_TSLU_IN_SECS : timeDiffInSecs;
    return timeDiffInSecs;
  };

  static renderTimeSinceLastUpdate = (timeDiffInSecs: number) => {
    if (timeDiffInSecs && timeDiffInSecs > 0) {
      const timeDiffFormatted = new Timespan(timeDiffInSecs * 1000).toString('HH:mm:ss');
      return TicketList.countUpTimer(
        timeDiffFormatted,
        timeDiffInSecs > ALERT_THRESHOLD_TSLU_IN_SECS,
      );
    }
    return '';
  };

  linkToOrderDetails = (row: TicketDto) => {
    if (!row.order?.url) {
      return '';
    }

    const orderId = getIdFromUrl(row.order.url);
    return (
      <span className={styles.text} key={row.id} title={`${row.order.orderNumber}`}>
        <Link
          to={`/geotrax/orders/all/${orderId}`}
          onClick={this.props.closePopover}
          className={styles.orderLink}
        >
          {row.order.orderNumber}
        </Link>
      </span>
    ) as JSX.Element;
  };

  getPlantName = (plantUrl: string) => {
    const foundPlant = this.props.plantList!.find(plant => plant.url === plantUrl);
    return (foundPlant ? foundPlant.name : 'N/A') as string;
  };

  getColumns = () => {
    const { ticketStateValue } = this.state;
    const { currentColumnsOverride, pastColumnsOverride } = this.props;
    const columnsProps: GetColumnsProps = {
      positions: this.props.positions!,
      getSecondsSinceLastUpdate: TicketList.getSecondsSinceLastUpdate,
      renderTimeSinceLastUpdate: TicketList.renderTimeSinceLastUpdate,
      getPlantName: this.getPlantName,
      columnWidths: [],
      orderDetails: this.linkToOrderDetails,
      headerStyle,
      cellStyle,
      isScaleTrax: false,
      // DEV-4153: page-level fact computed once per render, consumed by the
      // incoming configs' Received column — keeps the per-row cell free of
      // store subscriptions.
      hasEditAccess: hasFeatureFlagEditAccess(
        this.props.userPermission,
        this.props.currentRegion?.url ?? '',
        INCOMING_MATERIALS_FLAG,
      ),
    };
    if (ticketStateValue === TicketStateValue.Active) {
      return currentColumnsOverride
        ? currentColumnsOverride(columnsProps)
        : currentTicketColumns(columnsProps);
    }
    return pastColumnsOverride
      ? pastColumnsOverride(columnsProps)
      : pastTicketColumns(columnsProps);
  };

  setSearchTerm = (searchTerm: string) => {
    this.setState({ searchTerm }, this.saveFilters);
  };

  static countUpTimer = (timeDiffFormatted: string, isThresholdTSLUExceeded: boolean) => {
    if (isThresholdTSLUExceeded) {
      return (
        <div style={{ fontWeight: 'bold', color: '#BD0029' }}>
          <i className={cx('icon-warning')} style={{ marginRight: '5px' }} />
          {timeDiffFormatted}
        </div>
      );
    }
    return timeDiffFormatted;
  };

  // Forced rerender to refresh Time Since Last Update
  refreshTimeSinceLastUpdate() {
    this.setState({
      timeSinceLastUpdateCounter: this.state.timeSinceLastUpdateCounter + 1,
    });
  }

  ticketSelectHandler = (selectedTicket?: TicketDto) => {
    this.props.clearSelectedTicket();
    this.props.selectTicket(selectedTicket);
  };

  clearSearchText = () => {
    this.setState({ searchTerm: '' }, this.saveFilters);
  };

  ticketPopUp = (rowInfo: RowInfo) => {
    if (rowInfo?.original?.position?.location) {
      const truckToPop = {
        lat: rowInfo.original.position.location.latitude!,
        lng: rowInfo.original.position.location.longitude!,
        markerId: rowInfo?.original?.position?.trackedObject?.id?.toString(),
        truckId: rowInfo?.original?.position?.trackedObject?.truckAlias,
      };

      this.props.selectTruckMarker(truckToPop, rowInfo?.original?.url);
    }
  };

  rowConfiguration = (state: any, rowInfo: RowInfo) => {
    const { selected } = this.state;

    if (!rowInfo) {
      return {};
    }

    const classNames = [];
    if (rowInfo.viewIndex === selected) {
      classNames.push('selected');
    }

    return {
      onClick: () => {
        this.ticketSelectHandler(rowInfo.original);
        this.ticketPopUp(rowInfo);
        this.setState({
          selected: rowInfo.viewIndex,
        });
      },
      className: cx(classNames),
    };
  };

  // Helper used by tests to derive selected plant urls from plantMapFilters
  // eslint-disable-next-line react/no-unused-class-component-methods
  getPlantsFilter = (): string[] => this.props.selectedPlantUrlsList ?? [];

  renderDateRange = () => {
    const { ticketStateValue, filterDateStart, filterDateEnd } = this.state;

    const { ticketStateSwitcherType } = this.props;

    if (ticketStateValue === TicketStateValue.Active) {
      return null;
    }

    const dateRangePickerClass =
      ticketStateSwitcherType === TicketStateSwitcherType.DropDown
        ? styles.dateRangePickerBlock
        : undefined;

    return (
      <div className={styles.dateRangePicker}>
        {ticketStateSwitcherType === TicketStateSwitcherType.DropDown && (
          <label htmlFor="dateRangePicker">Date Range</label>
        )}
        <div className={dateRangePickerClass}>
          <DateRangePicker
            startDate={filterDateStart}
            endDate={filterDateEnd}
            shouldDisableFuture
            onChange={this.onDateRangeChange}
            minDate="1920-01-01"
            maxDate={DateWrapper.now.startOfDay.toString()}
            hideLabel={ticketStateSwitcherType === TicketStateSwitcherType.DropDown}
          />
        </div>
      </div>
    );
  };

  onDateRangeChange = (drpState: DateRangePickerState) => {
    const { startDate, endDate } = drpState;

    if (startDate && endDate) {
      this.setState(
        {
          filterDateStart: startDate,
          filterDateEnd: endDate,
        },
        this.saveFilters,
      );
    }
  };

  renderSearchTerm = () => {
    const { ticketStateSwitcherType, searchPlaceholder, searchMaxLength } = this.props;
    return (
      <div>
        {ticketStateSwitcherType === TicketStateSwitcherType.DropDown && (
          <label htmlFor="searchTerm">&nbsp;</label>
        )}
        <TicketSearch
          setTerm={this.setSearchTerm}
          clearSearchText={this.clearSearchText}
          searchTerm={this.state.searchTerm}
          placeholder={searchPlaceholder}
          maxLength={searchMaxLength}
        />
      </div>
    );
  };

  renderRefreshList = () => {
    const { ticketStateSwitcherType } = this.props;
    const refreshClass =
      ticketStateSwitcherType === TicketStateSwitcherType.DropDown
        ? styles.refreshBlock
        : undefined;

    return (
      <div className={refreshClass}>
        <RefreshCurrentTicketList onClick={this.onRefreshList} />
      </div>
    );
  };

  onRefreshList = () => {
    const { currentRegion, selectedProductLine } = this.props;
    const { selectedPlantUrlsList: plantsFilter } = this.props;
    this.props.fetchCurrentTicketList(
      currentRegion?.url,
      selectedProductLine,
      undefined,
      plantsFilter,
    );
    this.forceUpdate();
  };

  onChangeTicketState = (ticketStateValue: TicketStateValue) => {
    this.setState({ ticketStateValue }, this.saveFilters);
  };

  renderActiveTickets = () => {
    const { searchTerm } = this.state;
    const { activeTicketsOverride, ticketsList, defaultSortOverride } = this.props;
    const defaultSorted = defaultSortOverride ?? [
      {
        id: 'truckAliasCurrent',
        asc: true,
      },
    ];

    const data = filterBySearchTerm(activeTicketsOverride ?? ticketsList ?? [], searchTerm);
    const dataCount = data?.length ?? 0;
    const columns = dataCount > 0 ? this.getColumns() : [];

    return (
      <ReactTableFixedColumns
        data={data}
        columns={columns}
        defaultSorted={defaultSorted}
        getTrGroupProps={(state: any, rowInfo: RowInfo) => this.rowConfiguration(state, rowInfo)}
        onResizedChange={() => {}}
        showPagination={false}
        pageSize={dataCount}
        minRows={0}
        className="-highlight"
        noDataText="No results found"
      />
    );
  };

  backendRowClick = (rowInfo?: any) => {
    this.ticketSelectHandler(rowInfo.original);
    this.ticketPopUp(rowInfo);
    this.setState({
      selected: rowInfo.viewIndex,
    });
  };

  static dateTimeFormatter = (inputString: string): string =>
    inputString.replace(/T/, ' ').replace(/\.000Z/, '');

  renderBackendTickets = () => {
    const { selectedProductLine, ticketStateSwitcherType, destinationPlantUrls } = this.props;
    const { searchTerm, ticketStateValue, filterDateStart, filterDateEnd } = this.state;

    // DEV-3820: a provided destinationPlantUrls is authoritative even when empty. With IM
    // plants loaded, [] is a deliberate deselect-all; with none loaded (fetch pending or
    // failed) there are no plants to query. Either way list no past tickets and skip the
    // fetch: an empty param serializes to no plant filter, and falling back to the main map's
    // product-line-scoped selectedPlantUrlsList would leak the wrong scope.
    if (destinationPlantUrls?.length === 0) {
      return (
        <div className={styles.backendTicketsTable}>
          <ReactTableFixedColumns
            data={[]}
            columns={[]}
            showPagination={false}
            minRows={0}
            noDataText="No results found"
          />
        </div>
      );
    }

    const isDestinationFiltered = !!this.props.useDestinationPlantsFilter;

    const props: any = {
      backendTickets: true,
      baseUrl: this.baseUrl,
      columns: this.getColumns(),
      queryParams: {},
      header: 'Tickets',
      customHeader: <></>,
      onRowClick: this.backendRowClick,
      source: DATASOURCE.TICKET_LIST,
      ...(this.props.defaultSortOverride?.[0] && {
        defaultSortedId: this.props.defaultSortOverride[0].id,
      }),
      ...(isDestinationFiltered && { noRegionFilter: true }),
    };

    if (
      selectedProductLine &&
      ticketStateSwitcherType === TicketStateSwitcherType.Tabs &&
      !isDestinationFiltered
    ) {
      props.queryParams.plantProductLine = selectedProductLine;
    }

    if (filterDateStart) {
      props.queryParams.startDate = TicketList.dateTimeFormatter(
        new DateWrapper(filterDateStart).toISOString(),
      );
    }
    if (filterDateEnd) {
      props.queryParams.endDate = TicketList.dateTimeFormatter(
        new DateWrapper(filterDateEnd).toISOString(),
      );
    }
    if (searchTerm) {
      props.queryParams.searchTerm = searchTerm;
    }

    if (ticketStateValue === TicketStateValue.Closed) {
      // Include closed tickets and all voided tickets (even if not closed) in past tickets
      props.queryParams.isClosedOrVoided = true;
    }
    if (ticketStateValue === TicketStateValue.Voided) {
      props.queryParams.voided = true;
    }
    if (ticketStateValue === TicketStateValue.Open) {
      props.queryParams.closed = false;
      props.queryParams.voided = false;
    }

    if (destinationPlantUrls) {
      props.queryParams.destinationPlants = destinationPlantUrls;
    } else if (!isEmptyArray(this.props.selectedPlantUrlsList)) {
      const paramKey = this.props.useDestinationPlantsFilter ? 'destinationPlants' : 'plants';
      props.queryParams[paramKey] = this.props.selectedPlantUrlsList;
    }

    return (
      <div className={styles.backendTicketsTable}>
        <DataTableContent {...props} />
      </div>
    );
  };

  renderLeftSearchFields = () => {
    const { ticketStateSwitcherType } = this.props;

    if (ticketStateSwitcherType === TicketStateSwitcherType.Tabs) {
      return null;
    }

    return (
      <>
        {this.renderDateRange()}
        {this.renderRefreshList()}
      </>
    );
  };

  renderRightSearchFields = () => {
    const { ticketStateSwitcherType } = this.props;

    if (ticketStateSwitcherType === TicketStateSwitcherType.Tabs) {
      return (
        <>
          {this.renderDateRange()}
          {this.renderSearchTerm()}
          {this.renderRefreshList()}
        </>
      );
    }

    return this.renderSearchTerm();
  };

  render() {
    const { ticketStateValue } = this.state;
    const { ticketStateSwitcherType } = this.props;

    const searchDivClass =
      ticketStateSwitcherType === TicketStateSwitcherType.DropDown
        ? cx(styles.searchDiv, styles.searchDivSpace)
        : styles.searchDiv;

    return (
      <div className={cx(styles.ticketListContainer)}>
        <div className={cx(searchDivClass)}>
          <HeaderAndContainer
            elementsLeft={
              <div className={styles.leftSearchContainer}>
                <TicketStateSwitcher
                  onChange={this.onChangeTicketState}
                  switcherType={ticketStateSwitcherType}
                  initialValue={ticketStateValue}
                />
                {this.renderLeftSearchFields()}
              </div>
            }
            elementsRight={this.renderRightSearchFields()}
          />
        </div>
        <Paper id="ticketsPopover-paperElement" className={styles.paper}>
          {ticketStateValue === TicketStateValue.Active && this.renderActiveTickets()}
          {ticketStateValue !== TicketStateValue.Active && this.renderBackendTickets()}
        </Paper>
      </div>
    );
  }
}

interface TruckToPop {
  lat: number;
  lng: number;
  markerId?: string;
  truckId?: string;
}

export interface TicketListProps {
  positions?: PositionDto[];
  clearSelectedTicket: VoidFunc;
  selectTicket: (ticket?: TicketDto) => void;
  currentRegion: UrlKeyDto;
  currentPlant?: PlantDto;
  currentUser?: string;
  plantList?: PlantDto[];
  selectTruckMarker: (truckToPop: TruckToPop, ticketUrl?: string, truckAlias?: string) => void;
  selectedProductLine: ProductLine;
  closePopover: VoidFunc;
  selectedStartDate?: Date;
  selectedEndDate?: Date;
  updateSelectedDate?: (dateRange: DateRangePickerState) => void;
  ticketStateSwitcherType: TicketStateSwitcherType;
  ticketsList: TicketDto[];
  fetchCurrentTicketList: (
    regionUrl?: string,
    selectedProductLine?: string,
    plantUrl?: string,
    plantsFilter?: string[],
  ) => void;
  getDefaultPlant: ConnectedDispatchFunction<typeof getDefaultPlant>;
  setCurrentPlant: ConnectedDispatchFunction<typeof setCurrentPlant>;
  saveFilters: (filters: TicketListFilters) => void;
  ticketListFilters: TicketListFilters | null;
  searchPlaceholder?: string;
  searchMaxLength?: number;
  isScaleTrax?: boolean;
  plantMapFilters?: PlantFilterOption[];
  selectedPlantUrlsList: string[];
  currentColumnsOverride?: (props: GetColumnsProps) => ColumnProjection[];
  pastColumnsOverride?: (props: GetColumnsProps) => ColumnProjection[];
  defaultSortOverride?: { id: string; desc?: boolean }[];
  useDestinationPlantsFilter?: boolean;
  /**
   * Destination plants for the past-ticket queries. Omit to fall back to the main map's
   * `selectedPlantUrlsList`; an empty array means no plants, so no tickets (DEV-3820).
   */
  destinationPlantUrls?: string[];
  activeTicketsOverride?: TicketDto[];
  /** DEV-4153: per-region flag access map; feeds the Received column's Edit gate. */
  userPermission?: RegionAccessMap;
}

export interface TicketListState {
  timeSinceLastUpdateCounter: number;
  searchTerm: string;
  selected: any;
  timeSinceLastUpdateTimer: NodeJS.Timeout;
  ticketStateValue: TicketStateValue;
  filterDateStart: Date;
  filterDateEnd: Date;
}

const mapStateToProps = (state: any) => {
  const selectedPlantUrlsList = selectedPlantUrlsSelector(state);
  const ticketsList = ticketsMatchingMapFiltersSelector(state, selectedPlantUrlsList);
  return {
    currentRegion: state.currentRegion,
    currentPlant: state.currentPlant,
    currentUser: state.userUrl,
    selectedProductLine: state.selectedProductLine,
    positions: state.positionList,
    plantList: state.plantList,
    ticketListFilters: state.ticketListFilters,
    plantMapFilters: state.mapFilters.plantList,
    selectedPlantUrlsList,
    ticketsList,
    userPermission: state.userPermission,
  };
};

export default connect<any, any, any>(mapStateToProps, {
  selectTicket,
  clearSelectedTicket,
  selectMarker,
  openPopup,
  fetchCurrentTicketList,
  setCurrentTicketsColumns,
  setCurrentPlant,
  getDefaultPlant,
  selectTruckMarker,
  saveFilters,
})(TicketList);
